/**
 * Product-page behaviour for the ZenGUARD product section and blocks.
 *
 * <zg-product-information> (sections/zg-product-information.liquid, the section root)
 *   Variant snap. Horizon lists every value of every option and fetches whatever combination the
 *   shopper picks. When a pick in an earlier option (Depth) leaves a later one (Size) on a value
 *   that doesn't exist with it, Horizon would show "Unavailable". Once that fetch has settled (the
 *   picker is re-rendered, with data-variant-id only on values that exist), this selects the first
 *   existing value of the last later option that has one (Pack before Size with a third option),
 *   preferring one in stock, with a single change event on that input. Horizon's picker then
 *   fetches the real variant and updates the URL, price and form as for any pick. Events from
 *   product cards and quick-add dialogs are ignored.
 *
 * <zg-variant-text> (blocks/zg-product-heading.liquid: the spec line)
 *   Server-rendered text that depends on the selected variant. On ProductSelectEvent it morphs its
 *   children from the same element in the page the picker fetched (keyed by data-block-id).
 */
import { StandardEvents } from '@shopify/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * @typedef {Event & { detail?: { variantId?: string, optionValueId?: string }, promise?: Promise<any> }} ProductSelect
 */

class ZgProductInformation extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** True while this element dispatches its own change, so the event that follows can't snap again. */
  #snapping = false;

  connectedCallback() {
    this.#abort = new AbortController();
    this.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  /** @param {Event} event */
  #onProductSelect = (event) => {
    if (this.#snapping) return;

    const picker = event.target;
    if (!(picker instanceof HTMLElement) || picker.localName !== 'variant-picker') return;
    if (picker.dataset.templateProductMatch !== 'true' || picker.closest('product-card, quick-add-dialog, dialog')) return;
    if (picker.closest('zg-product-information') !== this) return;

    const { detail, promise } = /** @type {ProductSelect} */ (event);
    // A non-empty variantId means the picked combination exists.
    if (!detail || detail.variantId || !promise) return;

    const pickedValueId = detail.optionValueId ?? '';

    promise
      .then(() => this.#snap(picker, pickedValueId))
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[zg-product-information] Variant snap skipped:', error);
      });
  };

  /**
   * @param {HTMLElement} picker - The re-rendered main variant picker.
   * @param {string} pickedValueId - Option value id of the shopper's pick.
   */
  #snap(picker, pickedValueId) {
    if (!picker.isConnected) return;

    const fieldsets = [...picker.querySelectorAll('fieldset.variant-option')];
    if (fieldsets.length < 2) return;

    // Only fix an option after the one picked in (never the shopper's own pick; with no known pick, any but the
    // first). The last such option that has an existing value is fixed, so with a third option (Pack) the pack
    // changes before the size does. With two options this is always the last one.
    const picked = pickedValueId
      ? fieldsets.findIndex((fieldset) =>
          fieldset.querySelector(`input[data-option-value-id="${CSS.escape(pickedValueId)}"]`)
        )
      : -1;

    for (const fieldset of fieldsets.slice(Math.max(picked + 1, 1)).reverse()) {
      const target =
        fieldset.querySelector('input[type="radio"][data-variant-id][data-option-available="true"]') ??
        fieldset.querySelector('input[type="radio"][data-variant-id]');
      if (!(target instanceof HTMLInputElement)) continue;
      if (target.checked) return;

      // One change event: VariantPicker marks the input checked and fetches with every option's value.
      // Don't call its updateSelectedOption here (it would record the same input twice).
      target.checked = true;
      this.#snapping = true;
      try {
        target.dispatchEvent(new Event('change', { bubbles: true }));
      } finally {
        this.#snapping = false;
      }
      return;
    }
  }
}

class ZgVariantText extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** Counts variant selections; only the latest one's response may morph. */
  #generation = 0;

  connectedCallback() {
    this.#abort = new AbortController();
    this.closest('.shopify-section, dialog')?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, {
      signal: this.#abort.signal,
    });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  /** @param {Event} event */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const { promise } = /** @type {ProductSelect} */ (event);
    if (!promise) return;

    // The variant snap above starts a new selection inside the previous one's resolution, before this
    // listener's .then runs; skipping superseded responses keeps the non-existent combination off screen.
    const generation = ++this.#generation;

    promise
      .then(({ detail }) => {
        if (!detail?.html || !this.isConnected) return;

        if (detail.newProduct) {
          this.dataset.productId = String(detail.newProduct.id);
        } else if (detail.productId && String(detail.productId) !== this.dataset.productId) {
          return;
        }

        // Product bookkeeping above still applies to a superseded response; the morph doesn't.
        if (generation !== this.#generation) return;

        const blockId = CSS.escape(this.dataset.blockId ?? '');
        const fresh = detail.html.querySelector(`zg-variant-text[data-block-id="${blockId}"]`);
        if (fresh) morph(this, fresh, MORPH_OPTIONS);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[zg-variant-text] Variant update failed:', error);
      });
  };
}

if (!customElements.get('zg-product-information')) {
  customElements.define('zg-product-information', ZgProductInformation);
}

if (!customElements.get('zg-variant-text')) {
  customElements.define('zg-variant-text', ZgVariantText);
}
